export enum TicketPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent'
}

export enum TicketStatus {
  OPEN = 'open',
  IN_PROGRESS = 'in_progress',
  RESOLVED = 'resolved',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum TicketMessageType {
  COMMENT = 'comment',
  STATUS_CHANGE = 'status_change',
  ASSIGNMENT = 'assignment',
  SCHEDULED = 'scheduled',
  SYSTEM = 'system'
}

export interface TicketStage {
  id: number;
  name: string;
  description?: string;
  order_index: number;
  is_default: boolean;
  color: string;
  created_at: string;
  updated_at: string;
  tickets?: Ticket[];
}

export interface TicketReason {
  id: number;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface Ticket {
  id: number;
  client_id: number;
  reason_id: number;
  stage_id: number;
  title: string;
  description: string;
  created_at: string;
  assigned_at?: string;
  resolved_at?: string;
  completed_at?: string;
  created_by?: number;
  assigned_to?: number;
  priority: TicketPriority;
  status: TicketStatus;
  feedback_sent_to_client: boolean;
  problem_recurred: boolean;
  final_observations?: string;
  updated_at: string;
  updated_by?: number;
  is_viewed?: boolean;
  resolution_time_hours?: number;
  resolution_time_set_at?: string;
  resolution_time_paused_at?: string;
  remaining_hours?: number;
  moved_to_stage_at?: string; // Data em que o ticket foi movido para a etapa atual
  due_date?: string | null; // Prazo de conclusão (YYYY-MM-DD) — opcional
  
  // Relacionamentos
  client?: {
    id: number;
    legal_name: string;
    trade_name?: string;
    company_tax_id?: string;
    email?: string;
  };
  stage?: TicketStage;
  reason?: TicketReason;
  assignedUser?: {
    id: number;
    name: string;
    email: string;
  };
  creator?: {
    id: number;
    name: string;
    email: string;
  };
  messages?: TicketMessage[];
  coAssignees?: Array<{ id: number; name: string; email: string }>;
  attachments?: TicketAttachment[]; // anexos enviados na criação (sem mensagem associada)
}

export interface TicketAttachment {
  id: number;
  ticket_id: number;
  message_id?: number | null;
  file_name: string;
  file_url: string;
  mime_type: string;
  media_type: 'image' | 'video' | 'audio' | 'document';
  file_size?: number | null;
  uploaded_by?: number | null;
  created_at: string;
}

export interface TicketMessage {
  id: number;
  ticket_id: number;
  user_id?: number;
  message_type: TicketMessageType;
  content: string;
  created_at: string;
  updated_at?: string;
  scheduled_for?: string;
  old_stage_id?: number;
  new_stage_id?: number;

  // Relacionamentos
  user?: {
    id: number;
    name: string;
    email: string;
  };
  oldStage?: TicketStage;
  newStage?: TicketStage;
  attachments?: TicketAttachment[];
}

export interface CreateTicketDTO {
  client_id: number;
  reason_id: number;
  stage_id?: number;
  title: string;
  description: string;
  priority?: TicketPriority;
  assigned_to?: number;
  due_date?: string | null;
  co_assignees?: number[];
}

export interface UpdateTicketDTO {
  title?: string;
  description?: string;
  reason_id?: number;
  stage_id?: number;
  priority?: TicketPriority;
  assigned_to?: number;
  status?: TicketStatus;
  final_observations?: string;
  resolution_time_hours?: number;
  due_date?: string | null;
  co_assignees?: number[];
}

export interface CreateTicketStageDTO {
  name: string;
  description?: string;
  order_index?: number;
  color?: string;
}

export interface UpdateTicketStageDTO {
  name?: string;
  description?: string;
  order_index?: number;
  color?: string;
}

export interface CreateTicketReasonDTO {
  name: string;
  description?: string;
}

export interface UpdateTicketReasonDTO {
  name?: string;
  description?: string;
}

export interface CreateTicketMessageDTO {
  content: string;
  message_type?: TicketMessageType;
  scheduled_for?: string;
  mentioned_user_ids?: number[];
}

export interface TicketChecklistDTO {
  feedback_sent_to_client: boolean;
  problem_recurred: boolean;
}

export interface TicketMetrics {
  total_tickets: number;
  avg_resolution_hours: number;
  by_reason: Array<{
    reason_id: number;
    reason_name: string;
    count: number;
    avg_resolution_hours: number;
  }>;
}

/**
 * Filtro aplicado a tickets (usado tanto pelo filtro global quanto pelo de cada coluna).
 * priority: prioridade específica, 'all' (todas, ignora prioridade) ou null (sem filtro de prioridade).
 */
export type TicketFilter = {
  priority: TicketPriority | 'all' | null;
  assignedToMe: boolean;
};

export const EMPTY_TICKET_FILTER: TicketFilter = {
  priority: null,
  assignedToMe: false,
};

export type TicketNotificationType = 'ASSIGNED' | 'MENTION';

export interface TicketNotificationItem {
  key: string;
  type: TicketNotificationType;
  ticket_id: number;
  ticket_title: string;
  stage_name: string | null;
  stage_color: string | null;
  actor_id: number | null;
  actor_name: string | null;
  message_id: number | null;
  excerpt: string | null;
  created_at: string;
  is_read: boolean;
}

